import { useTranslation } from 'react-i18next';
import { Eye, Search, Star, Heart } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Separator } from '@/components/atoms/separator';

interface ProductMetrics {
  views: number;
  dailyViews: number;
  weeklyViews: number;
  monthlyViews: number;
  searchAppearances: number;
}

interface ProductReviews {
  averageRating: number;
  totalReviews?: number;
  ratingDistribution?: Record<number, number>;
}

interface ProductMetricsDisplayProps {
  metrics?: ProductMetrics;
  reviews?: ProductReviews;
  likedCount?: number;
}

export function ProductMetricsDisplay({ metrics, reviews, likedCount }: ProductMetricsDisplayProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Metrics Card */}
      {metrics && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="size-5" />
              {t('products.metrics')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Eye className="size-4" />
                <span>{t('products.totalViews')}</span>
              </div>
              <span className="font-bold">{metrics.views}</span>
            </div>
            <Separator />
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold">{metrics.dailyViews}</p>
                <p className="text-xs text-muted-foreground">{t('products.daily')}</p>
              </div>
              <div>
                <p className="text-lg font-bold">{metrics.weeklyViews}</p>
                <p className="text-xs text-muted-foreground">{t('products.weekly')}</p>
              </div>
              <div>
                <p className="text-lg font-bold">{metrics.monthlyViews}</p>
                <p className="text-xs text-muted-foreground">{t('products.monthly')}</p>
              </div>
            </div>
            {metrics.searchAppearances > 0 && (
              <>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Search className="size-4" />
                    <span>{t('products.searchAppearances')}</span>
                  </div>
                  <span className="font-bold">{metrics.searchAppearances}</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reviews Card */}
      {reviews && typeof reviews.averageRating === 'number' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="size-5" />
              {t('products.reviews')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Star className="size-8 fill-yellow-400 text-yellow-400" />
              <span className="text-3xl font-bold">{reviews.averageRating.toFixed(1)}</span>
            </div>
            <p className="text-center text-muted-foreground">
              {reviews.totalReviews ?? 0} {t('products.totalReviews')}
            </p>
            {reviews.ratingDistribution && (
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = reviews.ratingDistribution?.[rating] || 0;
                  const totalReviews = reviews.totalReviews ?? 0;
                  const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                  return (
                    <div key={rating} className="flex items-center gap-2">
                      <span className="text-sm w-3">{rating}</span>
                      <Star className="size-3 fill-yellow-400 text-yellow-400" />
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div
                          className="bg-yellow-400 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground w-8">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Likes Card */}
      {likedCount !== undefined && likedCount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="size-5" />
              {t('products.likes')}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="flex items-center justify-center gap-2">
              <Heart className="size-8 fill-red-500 text-red-500" />
              <span className="text-3xl font-bold">{likedCount}</span>
            </div>
            <p className="text-muted-foreground mt-2">{t('products.customersLiked')}</p>
          </CardContent>
        </Card>
      )}
    </>
  );
}
